import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  RemoveCircle as UnassignIcon,
  Visibility as ViewIcon,
  AccessTime as AccessTimeIcon,
  PriorityHigh as PriorityHighIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';
import TherapistAssessmentGrants from './TherapistAssessmentGrants';
import TherapistSimulationGrants from './TherapistSimulationGrants';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function TherapistDetailView({ token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { therapistId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [therapistData, setTherapistData] = useState(null);
  // Tab indices: 0=Clients, 1=Consultations, 2=Assessments, 3=Simulations.
  // Honour location.state.focusTab so callers (e.g. Supervisor Simulations
  // overview) can deep-link straight to the relevant tab.
  const tabIndexByName = { clients: 0, consultations: 1, assessments: 2, simulations: 3 };
  const initialTab = tabIndexByName[location.state?.focusTab] ?? 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [unassignDialog, setUnassignDialog] = useState(false);
  const [unassigning, setUnassigning] = useState(false);

  const loadTherapistData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getTherapistDetails(therapistId, token);
      setTherapistData(data);
    } catch (err) {
      console.error('Error loading therapist details:', err);
      setError(err.response?.data?.msg || 'Failed to load therapist details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (therapistId) {
      loadTherapistData();
    }
  }, [therapistId, token]);

  const handleUnassign = async () => {
    try {
      setUnassigning(true);
      setError('');
      await clinicalSupervisorApi.unassignTherapist(therapistId, token);
      setSuccess('Therapist unassigned successfully');
      setUnassignDialog(false);
      // Navigate back after short delay
      setTimeout(() => {
        navigate('/clinical-supervisor/therapists');
      }, 1500);
    } catch (err) {
      console.error('Error unassigning therapist:', err);
      setError(err.response?.data?.msg || 'Failed to unassign therapist');
    } finally {
      setUnassigning(false);
    }
  };

  const handleViewConsultation = (consultationId) => {
    navigate(`/clinical-supervisor/consultations/${consultationId}`);
  };

  const handleViewClient = (clientId) => {
    navigate(`/clinical-supervisor/therapists/${therapistId}/clients/${clientId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !therapistData) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/clinical-supervisor/therapists')} sx={{ mt: 2 }}>
          Back to Therapists
        </Button>
      </Box>
    );
  }

  const { therapist, clients, consultations, stats } = therapistData || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/clinical-supervisor/therapists')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5">Therapist Details</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <IconButton onClick={loadTherapistData}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="error"
            startIcon={<UnassignIcon />}
            onClick={() => setUnassignDialog(true)}
          >
            Unassign Therapist
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Therapist Info Card */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="flex-start" gap={2} mb={3}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
                {therapist?.name?.[0]?.toUpperCase() || 'T'}
              </Avatar>
              <Box flex={1}>
                <Typography variant="h4" gutterBottom>
                  {therapist?.name}
                </Typography>
                <Chip
                  label={therapist?.status || 'Active'}
                  size="small"
                  color={therapist?.status === 'Active' ? 'success' : 'default'}
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{therapist?.email}</Typography>
                    </Box>
                    {therapist?.therapistInfo?.phoneNumber && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {therapist.therapistInfo.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                    {therapist?.therapistInfo?.location && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{therapist.therapistInfo.location}</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {therapist?.therapistInfo?.licenseNumber && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BadgeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {therapist.therapistInfo.licenseType || 'License'}:{' '}
                          {therapist.therapistInfo.licenseNumber}
                        </Typography>
                      </Box>
                    )}
                    {therapist?.therapistInfo?.specializations?.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Specializations:
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {therapist.therapistInfo.specializations.map((spec, idx) => (
                            <Chip key={idx} label={spec} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {therapist?.therapistInfo?.bio && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" paragraph>
                  {therapist.therapistInfo.bio}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h3" color="primary.main">
                    {stats?.activeClientsCount || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Clients
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h3" color="success.main">
                    {stats?.consultationCount || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Consultations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h3" color="info.main">
                    {stats?.completedThisMonth || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed This Month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Paper>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Clients (${clients?.length || 0})`} />
          <Tab label={`Consultations (${consultations?.length || 0})`} />
          <Tab label="Assessments" />
          <Tab label="Simulations" />
        </Tabs>

        {/* Clients Tab */}
        <TabPanel value={tabValue} index={0}>
          {clients && clients.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Joined Date</TableCell>
                    <TableCell>Last Active (Client)</TableCell>
                    <TableCell>Last Therapist Interaction</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow
                      key={client._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewClient(client._id)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography>{client.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>
                        {new Date(client.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {client.lastActivity ? (
                          <Tooltip title={new Date(client.lastActivity).toLocaleString()}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTimeIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDistanceToNow(new Date(client.lastActivity), { addSuffix: true })}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Never</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.lastTherapistInteraction ? (
                          <Tooltip title={`${client.lastTherapistInteractionType} at ${new Date(client.lastTherapistInteraction).toLocaleString()}`}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTimeIcon fontSize="small" color="primary" />
                              <Typography variant="body2">
                                {formatDistanceToNow(new Date(client.lastTherapistInteraction), { addSuffix: true })}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No interaction</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.latestConversationStatus === 'human_review_requested' ? (
                          <Chip
                            icon={<PriorityHighIcon />}
                            label="Review Requested"
                            size="small"
                            color="warning"
                          />
                        ) : (
                          <Chip
                            label={client.status || 'Active'}
                            size="small"
                            color={client.status === 'Active' ? 'success' : 'default'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClient(client._id);
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No clients assigned
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Consultations Tab */}
        <TabPanel value={tabValue} index={1}>
          {consultations && consultations.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Topics</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultations.map((consultation) => (
                    <TableRow key={consultation._id} hover>
                      <TableCell>{consultation.clientName}</TableCell>
                      <TableCell>
                        {new Date(consultation.consultationDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={consultation.status}
                          size="small"
                          color={
                            consultation.status === 'completed'
                              ? 'success'
                              : consultation.status === 'in_progress'
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {consultation.topics?.slice(0, 3).map((topic, idx) => (
                            <Chip key={idx} label={topic} size="small" variant="outlined" />
                          ))}
                          {consultation.topics?.length > 3 && (
                            <Chip
                              label={`+${consultation.topics.length - 3}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewConsultation(consultation._id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No consultations yet
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Assessment grants tab */}
        <TabPanel value={tabValue} index={2}>
          <TherapistAssessmentGrants therapistId={therapist?._id || therapist?.id} token={token} />
        </TabPanel>

        {/* Simulation grants tab */}
        <TabPanel value={tabValue} index={3}>
          <TherapistSimulationGrants therapistId={therapist?._id || therapist?.id} token={token} />
        </TabPanel>
      </Paper>

      {/* Unassign Confirmation Dialog */}
      <Dialog open={unassignDialog} onClose={() => setUnassignDialog(false)}>
        <DialogTitle>Unassign Therapist</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unassign {therapist?.name}? This will remove them from your
            supervision but will not affect their active clients or consultations.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignDialog(false)}>Cancel</Button>
          <Button onClick={handleUnassign} color="error" disabled={unassigning}>
            {unassigning ? <CircularProgress size={24} /> : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TherapistDetailView;
